import classNames from 'classnames';
import styles from './index.module.sass';
import { ISellOption, SellOption } from './sell-option';
import {
  BestPriceBuyerItem,
  IBestPriceBagItem,
} from '@/modules/mixing/store/analytical-mixing.store';
import { useStore } from 'effector-react';
import { $selectedRecipeStore } from '@/modules/mixing/store/recipes.store';
import { createOfferFoo } from '../../utils/create-offer.function';
import { RecipesService } from '../../services/recipes.service';
import { $topRecipesForWarehouse } from '@/widgets/recipe/model';
import { OFFER_ACTION_TYPE } from '../../constants/trade-offers.constant';
import { warehouseModel } from '@/entities/warehouse';
import { WarehouseTypes } from '@/modules/warehouses/ts';

export const SellContentModal = ({
  offer,
  action,
  sleeveData,
  onCancel,
  modalViewOnly,
  onSuccess,
  isLeftover,
}: {
  action: 'hydrate' | 'drain';
  sleeveData: IBestPriceBagItem;
  offer: BestPriceBuyerItem;
  onCancel: () => void;
  modalViewOnly: boolean;
  onSuccess?: () => Promise<void>;
  isLeftover?: boolean;
}) => {
  const activeWarehouse = useStore($selectedRecipeStore);
  const recipesService = new RecipesService(offer, sleeveData);
  const hydartedCase = recipesService.calculateHydrateParams();
  const drainCase = recipesService.calculateDrainParams();
  const topRecipesData = useStore($topRecipesForWarehouse);
  const isWarehouseTypeSleeve = activeWarehouse?.type && Number(activeWarehouse.type) as unknown as WarehouseTypes === warehouseModel.WAREHOUSE_TYPES.sleeve;


  const freightPriceDifference =
    sleeveData.freight_cost_before_moisture_mod -
    sleeveData.freight_cost_after_moisture_mod;

  const numberToReadable = (val: number, digits?: number) =>
    val
      ? val.toLocaleString('ru', { maximumFractionDigits: digits || 0, minimumFractionDigits: 0 })
      : val;

  const data: { hydrate: ISellOption[]; drain: ISellOption[] } = {
    hydrate: [
      {
        title: 'продать как есть',
        tags: [
          { text: 'вес не изменится', color: '#DFE6FA' },
          {
            text: `стоимость логистики не изменится. Везем ${numberToReadable(
              sleeveData?.weight, 2
            )}т`,
            color: '#DFE6FA',
            display: !!sleeveData.freight_cost_tkm,
          },
        ],
        offerData: {
          weight: numberToReadable(+hydartedCase.original.weight, 2),
          grainPrice: numberToReadable(+hydartedCase.original.brutto),
          freightPrice: numberToReadable(+hydartedCase.original.freightPrice),
          totalPrice: numberToReadable(+hydartedCase.original.netto),
        },
        action: {
          onClick: async () =>
            await createOfferFoo({
              action: null,
              weight_gain_tons: 0,
              initial_weight_gain_tons: sleeveData.weight_gain_tons,
              price: offer.price_per_ton,
              weight: sleeveData.weight,
              sleeveData,
              onCancel,
              offer,
              warehouseId: Number(activeWarehouse?.id),
              additionalData: {
                action_place: null,
                mix_id: null,
                mixing_profit: null,
                price_logistic: hydartedCase.original.freightPrice,
                initial_price_logistic: offer.freight_price,
                price_netto: hydartedCase.original.netto,
                price_all: hydartedCase.original.brutto,
                is_remnants: !!isLeftover,
              },
              signature: isLeftover ? topRecipesData?.mix_signature : undefined,
            }),
          disabled: false,
        },
      },
      {
        title: 'увлажнить на пункте приема зерна',
        totalPriceColor: '#007E3C',
        tags: [
          {
            text: `вес на ППЗ увеличится на ${numberToReadable(
              sleeveData.weight_gain_tons, 2
            )} т`,
            color: '#B3D8C5',
          },
          {
            text: `стоимость логистики не изменится. Везем ${numberToReadable(
              sleeveData?.weight, 2
            )}т`,
            color: '#DFE6FA',
            display: !!sleeveData.freight_cost_tkm,
          },
        ],
        offerData: {
          weight: numberToReadable(+hydartedCase.hydrateAfter.weight, 2),
          grainPrice: numberToReadable(+hydartedCase.hydrateAfter.brutto),
          freightPrice: numberToReadable(+hydartedCase.hydrateAfter.freightPrice),
          totalPrice: numberToReadable(+hydartedCase.hydrateAfter.netto),
        },
        action: {
          onClick: async () =>
            await createOfferFoo({
              action: 'hydrate',
              price: offer.price_per_ton,
              weight: sleeveData.weight,
              weight_gain_tons: sleeveData.weight_gain_tons,
              initial_weight_gain_tons: sleeveData.weight_gain_tons,
              sleeveData,
              onCancel,
              offer,
              warehouseId: Number(activeWarehouse?.id),
              additionalData: {
                action_place: OFFER_ACTION_TYPE.RECEIVING_POINT,
                mix_id: null,
                mixing_profit: null,
                price_logistic: hydartedCase.hydrateAfter.freightPrice,
                initial_price_logistic: offer.freight_price,
                price_netto: hydartedCase.hydrateAfter.netto,
                price_all: hydartedCase.hydrateAfter.brutto,
                is_remnants: !!isLeftover,
              },
              signature: isLeftover ? topRecipesData?.mix_signature : undefined,
            }),
          disabled: false,
        },
      },
      {
        title: 'увлажнить на собственном складе',
        totalPriceColor: '#E73A42',
        tags: [
          {
            text: `вес увеличится на ${numberToReadable(
              sleeveData.weight_gain_tons, 2
            )} т`,
            color: '#B3D8C5',
          },
          {
            text: `стоимость логистики подорожает на ${numberToReadable(
              sleeveData.freight_cost_after_moisture_mod -
                sleeveData.freight_cost_before_moisture_mod,
            )} р. Везем ${numberToReadable(
              sleeveData.weight + sleeveData.weight_gain_tons, 2
            )}т`,
            color: '#F8C4C6',
            display: !!sleeveData.freight_cost_tkm,
          },
        ],
        offerData: {
          weight: numberToReadable(+hydartedCase.hydrateBefore.weight, 2),
          grainPrice: numberToReadable(+hydartedCase.hydrateBefore.brutto),
          freightPrice: numberToReadable(+hydartedCase.hydrateBefore.freightPrice),
          totalPrice: numberToReadable(+hydartedCase.hydrateBefore.netto),
        },
        action: {
          onClick: async () =>
            await createOfferFoo({
              action: 'hydrate',
              price: offer.price_per_ton,
              weight: sleeveData.weight,
              weight_gain_tons: sleeveData.weight_gain_tons,
              initial_weight_gain_tons: sleeveData.weight_gain_tons,
              sleeveData,
              onCancel,
              offer,
              warehouseId: Number(activeWarehouse?.id),
              additionalData: {
                action_place: OFFER_ACTION_TYPE.OWN_WAREHOUSE,
                mix_id: null,
                mixing_profit: null,
                price_logistic: hydartedCase.hydrateBefore.freightPrice,
                initial_price_logistic: offer.freight_price,
                price_netto: hydartedCase.hydrateBefore.netto,
                price_all: hydartedCase.hydrateBefore.brutto,
                is_remnants: !!isLeftover,
              },
              signature: isLeftover ? topRecipesData?.mix_signature : undefined,
            }),
          disabled: false,
        },
      },
    ],
    drain: [
      {
        title: 'осушить на пункте приема зерна',
        totalPriceColor: '#E73A42',
        tags: [
          {
            text: `вес на ППЗ уменьшится на ${numberToReadable(
              Math.abs(sleeveData.weight_gain_tons), 2
            )} т`,
            color: '#F8C4C6',
          },
          {
            text: `стоимость логистики не изменится. Везем ${sleeveData.weight}т`,
            display: !!sleeveData.freight_cost_tkm,
            color: '#DFE6FA',
          },
        ],
        offerData: {
          weight: numberToReadable(+drainCase.drainAfter.weight + +sleeveData.weight_gain_tons, 2),
          grainPrice: numberToReadable(+drainCase.drainAfter.brutto),
          freightPrice: numberToReadable(+drainCase.drainAfter.freightPrice),
          totalPrice: numberToReadable(+drainCase.drainAfter.netto),
        },
        action: {
          onClick: async () =>
            await createOfferFoo({
              action: 'drain',
              price: offer.price_per_ton,
              weight: sleeveData.weight,
              initial_weight_gain_tons: sleeveData.weight_gain_tons,
              weight_gain_tons: sleeveData.weight_gain_tons,
              sleeveData,
              onCancel,
              offer,
              warehouseId: Number(activeWarehouse?.id),
              additionalData: {
                action_place: OFFER_ACTION_TYPE.RECEIVING_POINT,
                mix_id: null,
                mixing_profit: null,
                price_logistic: hydartedCase.hydrateAfter.freightPrice,
                initial_price_logistic: offer.freight_price,
                price_netto: hydartedCase.hydrateAfter.netto,
                price_all: hydartedCase.hydrateAfter.brutto,
                is_remnants: !!isLeftover,
              },
              signature: isLeftover ? topRecipesData?.mix_signature : undefined,
            }),
          disabled: false,
        },
      },
      {
        title: 'осушить на собственном складе',
        totalPriceColor: '#007E3C',
        tags: [
          {
            text: `вес уменьшится на ${numberToReadable(
              Math.abs(sleeveData.weight_gain_tons), 2
            )} т`,
            color: '#F8C4C6',
          },
          {
            text: `стоимость логистики подешевеет на ${numberToReadable(
              freightPriceDifference,
            )} р. Везем ${numberToReadable(
              sleeveData.weight + sleeveData.weight_gain_tons, 2
            )}т`,
            color: recipesService.TAG_COLORS.GREEN,
            display: !!sleeveData.freight_cost_tkm,
          },
        ],
        offerData: {
          weight: numberToReadable(+drainCase.drainBefore.weight, 2),
          grainPrice: numberToReadable(+drainCase.drainBefore.brutto),
          freightPrice: numberToReadable(+drainCase.drainBefore.freightPrice),
          totalPrice: numberToReadable(+drainCase.drainBefore.netto),
        },
        action: {
          onClick: async () =>
            await createOfferFoo({
              action: 'drain',
              price: offer.price_per_ton,
              weight: sleeveData.weight,
              weight_gain_tons: sleeveData.weight_gain_tons,
              initial_weight_gain_tons: sleeveData.weight_gain_tons,
              sleeveData,
              onCancel,
              offer,
              warehouseId: Number(activeWarehouse?.id),
              additionalData: {
                action_place: OFFER_ACTION_TYPE.OWN_WAREHOUSE,
                mix_id: null,
                mixing_profit: null,
                price_logistic: hydartedCase.hydrateBefore.freightPrice,
                initial_price_logistic: offer.freight_price,
                price_netto: hydartedCase.hydrateBefore.netto,
                price_all: hydartedCase.hydrateBefore.brutto,
                is_remnants: !!isLeftover,
              },
              signature: isLeftover ? topRecipesData?.mix_signature : undefined,
            }),
          disabled: false,
        },
      },
    ],
  };
  return (
    <div className={styles.root}>
      <h2
        className={classNames(styles.modalHeader, action === 'drain' && styles.drain)}
      >
        {action === 'drain' ? 'осушение' : 'увлажнение'}
      </h2>
      <p>выберите наилучший для вас вариант</p>
      <div className={styles.optionsArea}>
        {data[action].map((item, i) => (
          <SellOption
            key={i}
            title={item.title}
            tags={item.tags}
            offerData={item.offerData}
            action={item.action}
            viewOnly={modalViewOnly}
            totalPriceColor={item.totalPriceColor}
            onSuccess={onSuccess}
            withoutLogisitic={!item.offerData.freightPrice}
            isWarehouseTypeSleeve={isWarehouseTypeSleeve}
          />
        ))}
      </div>
    </div>
  );
};
